import { ExifImageMetadataDto } from '@nusign/picflow-backend-types';

export const exifKeys = [
  'captureTime',
  'shutterSpeed',
  'aperture',
  'iso',
  'focalLength',
  'camera',
  'lens',
  'creator',
  'copyright',
];

export const getExifValue = (exif: ExifImageMetadataDto, param: string) => {
  if (param === 'captureTime') return getCaptureTime(exif);
  if (param === 'shutterSpeed') return getShutterSpeed(exif);
  if (param === 'aperture') return getAperture(exif);
  if (param === 'iso') return getIso(exif);
  if (param === 'focalLength') return getFocalLength(exif);
  if (param === 'camera') return getCamera(exif);
  if (param === 'lens') return getLens(exif);
  if (param === 'creator') return getCreator(exif);
  if (param === 'copyright') return getCopyright(exif);
};

export const getShutterSpeed = (exif: ExifImageMetadataDto) => {
  if (!exif) return;
  // "1/250", "2"
  const string = exif.ExposureTime as string;
  if (!string) return;
  return string + 's';
};

export const getAperture = (exif: ExifImageMetadataDto) => {
  if (!exif) return;
  // "f/22", "f/1.2", "71/10", "f/0"
  const string = exif.FNumber as string;
  if (!string) return;
  let value = 0;
  if (string.startsWith('f/')) value = parseFloat(string.split('f/')[1]);
  else if (string.includes('/')) {
    const vals = string.split('/').map(a => parseFloat(a));
    value = vals[0] / vals[1];
  }
  if (!value) return;

  return `f/${Math.round(value * 10) / 10}`;
};

export const getIso = (exif: ExifImageMetadataDto) => {
  if (!exif) return;
  const iso = exif.ISOSpeedRatings || exif.PhotographicSensitivity;
  if (!iso) return;
  return iso.toString();
};

export const getFocalLength = exif => {
  if (!exif) return;
  // "7.1 mm", "135 mm", "33/1"
  // Can also be 1000/100 (in that case we need to devide the number by 100)
  let string = exif.FocalLength;
  if (!string) return;
  const multiplier = string.includes('/') ? parseInt(string.split('/')[1]) || 1 : 1;
  if (string.includes('/')) string = `${string.split('/')[0]} mm`;
  if (!string.endsWith(' mm')) return;
  const value = parseFloat(string.split(' mm')[0]) / multiplier;
  return `${Math.round(value * 10) / 10} mm`;
};

export const getCamera = (exif: ExifImageMetadataDto) => {
  if (!exif) return;
  return exif.Model as string;
};

export const getLens = (exif: ExifImageMetadataDto) => {
  if (!exif) return;
  return exif.Lens as string;
};

export const getCreator = (exif: ExifImageMetadataDto) => {
  if (!exif) return;
  return (exif.creator || exif.Author) as string;
};

export const getCopyright = (exif: ExifImageMetadataDto) => {
  if (!exif) return;
  return exif.Copyright as string;
};

export const getCaptureTime = (exif: ExifImageMetadataDto) => {
  if (!exif) return;
  if (exif.CreateDate) {
    try {
      return new Date(exif.CreateDate).getTime();
    } catch (err) {
      console.warn(err);
    }
  }

  if (exif.DateTime) {
    const s = exif.DateTime as string;
    try {
      const year = Number(s.split(':')[0]);
      const month = Number(s.split(':')[1]) - 1;
      const day = Number(s.split(':')[2].split(' ')[0]);
      const hour = Number(s.split(' ')[1].split(':')[0]);
      const minute = Number(s.split(' ')[1].split(':')[1]);
      const second = Number(s.split(' ')[1].split(':')[2]);
      return new Date(year, month, day, hour, minute, second).getTime();
    } catch (err) {
      console.warn(err);
    }
  }
};

export const getExifDescription = (exif: ExifImageMetadataDto) => {
  if (!exif) return;
  const description = exif.ImageDescription;
  if (!description) return;
  if (description === '[Undefined encoding]') return;
  return description as string;
};

export const getExifRating = (exif: ExifImageMetadataDto) => {
  if (!exif) return;
  if (!exif.Rating) return;
  return parseFloat(exif.Rating as string);
};
